import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { portal as portalApi, segments as segmentsApi, photos as photosApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SegmentMiniMap from './SegmentMiniMap';
import { footingColor, footingDescription, FOOTING_SCORES } from '../utils/footing';
import { formatFeet } from '../utils/units';
import { uploadSegmentPhoto } from '../utils/photoUpload';

/**
 * "Your Trails" on the portal home: every segment the volunteer owns, with the
 * footing rating to give and a place to add photos. Owners rate their own
 * segments, so there's nothing to hunt for on the map.
 */
export default function MyTrails() {
  const [segments, setSegments] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { setSegments(await portalApi.mySegments()); } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const replace = (updated) => setSegments((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));

  if (error) return <section className="portal-section"><h2>Your Trails</h2><div className="alert alert-error">{error}</div></section>;
  if (!segments) return null;

  const rated = segments.filter((s) => s.footing_quality).length;

  return (
    <section className="portal-section">
      <div className="portal-section-head">
        <h2>Your Trails</h2>
        {segments.length > 0 && (
          <span className="portal-muted">{rated} of {segments.length} rated</span>
        )}
      </div>

      {segments.length === 0 ? (
        <p className="portal-muted">
          No trail segments are assigned to you yet. A committee chair sets trail owners — ask at the next meeting.
        </p>
      ) : (
        <>
          <p className="portal-muted my-trails-help">
            Rate each segment's <strong>footing</strong> from 1 to 10 — <strong>10</strong> means anyone could walk it
            easily, <strong>1</strong> is challenging for horses and people. Rate the trail itself, not this week's
            weather. Photos help the committee see what you see.
          </p>
          <div className="my-trails">
            {segments.map((s) => <TrailCard key={s.id} segment={s} onChange={replace} />)}
          </div>
        </>
      )}
    </section>
  );
}

function TrailCard({ segment, onChange }) {
  const { user, isAdmin, isChair } = useAuth();
  const [busy, setBusy] = useState(null);      // null | 'rating' | upload progress text
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  const rate = async (value) => {
    setBusy('rating'); setError(null);
    try {
      const updated = await segmentsApi.update(segment.id, { footing_quality: value === '' ? null : Number(value) });
      onChange({
        ...segment,
        footing_quality: updated.footing_quality,
        footing_rated_at: updated.footing_rated_at,
        footing_rated_by_name: updated.footing_quality ? user?.display_name : null,
      });
    } catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  const addPhotos = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setError(null);
    const added = [];
    const failures = [];
    for (let i = 0; i < list.length; i++) {
      setBusy(`Uploading ${list.length > 1 ? `${i + 1} of ${list.length}` : 'photo'}…`);
      try { added.push(await uploadSegmentPhoto(segment.id, list[i])); }
      catch (err) { failures.push(err.message); }
    }
    if (added.length) onChange({ ...segment, photos: [...added.reverse(), ...segment.photos] });
    if (failures.length) setError(failures.join(' '));
    setBusy(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const removePhoto = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await photosApi.delete(photo.id);
      onChange({ ...segment, photos: segment.photos.filter((p) => p.id !== photo.id) });
    } catch (err) { setError(err.message); }
  };

  const mapSegments = [{ segment_id: segment.id, segment_number: segment.segment_number, geometry: segment.geometry }];

  return (
    <div className="my-trail-card">
      <div className="my-trail-head">
        <div>
          <h3>{segment.segment_label || segment.trail_name} <span className="portal-muted">{segment.segment_label ? segment.trail_name : `#${segment.segment_number}`}</span></h3>
          <div className="my-trail-meta">
            {segment.length_feet && <span>📏 {formatFeet(segment.length_feet)}</span>}
            {segment.category === 'expansion' && <span className="category-badge category-expansion">expansion</span>}
          </div>
        </div>
        <Link className="btn-sm btn-secondary" to={`/?segment=${segment.id}`}>Open on map</Link>
      </div>

      {segment.geometry && <SegmentMiniMap trailName={segment.trail_name} segments={mapSegments} />}

      <label className="my-trail-rating">
        <span className="footing-dot" style={{ backgroundColor: footingColor(segment.footing_quality) }} />
        <strong>Footing</strong>
        <select value={segment.footing_quality ?? ''} onChange={(e) => rate(e.target.value)} disabled={busy === 'rating'}>
          <option value="">Not rated yet</option>
          {FOOTING_SCORES.map((n) => <option key={n} value={n}>{n} — {footingDescription(n)}</option>)}
        </select>
      </label>
      {segment.footing_quality && segment.footing_rated_at && (
        <p className="portal-muted my-trail-rated">
          Rated {new Date(segment.footing_rated_at).toLocaleDateString()}
          {segment.footing_rated_by_name && <> by {segment.footing_rated_by_name}</>}
        </p>
      )}

      <div className="my-trail-photos">
        {segment.photos.map((p) => (
          <div key={p.id} className="my-trail-photo">
            <a href={p.url} target="_blank" rel="noopener noreferrer"><img src={p.url} alt={p.caption || 'Trail photo'} loading="lazy" /></a>
            {(p.uploaded_by === user?.id || isAdmin || isChair) && (
              <button className="my-trail-photo-remove" onClick={() => removePhoto(p)} title="Delete photo">✕</button>
            )}
          </div>
        ))}
        <button className="my-trail-photo-add" onClick={() => fileInput.current && fileInput.current.click()} disabled={!!busy}>
          {busy && busy !== 'rating' ? busy : '📷 Add photos'}
        </button>
        <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(e.target.files)} />
      </div>

      {error && <div className="alert alert-error">{error}<button onClick={() => setError(null)}>✕</button></div>}
    </div>
  );
}
